import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Download,
  Print,
  AccessTime,
  Room,
  Person,
  Today,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useStudentTimetable } from "../../hooks/queries/useTimetable";
import scheduleService from "../../services/scheduleService";

const toDateString = (value) => {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  const y = safe.getFullYear();
  const m = String(safe.getMonth() + 1).padStart(2, "0");
  const day = String(safe.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Timetable = () => {
  const user = useSelector((state) => state.auth.user);
  const studentId = user?.studentId;
  const studentClassId =
    user?.classId ??
    user?.student?.classId ??
    user?.student?.class_id ??
    user?.studentClassId ??
    null;
  const studentBatchCodeRaw =
    user?.practicalBatchCode ??
    user?.student?.practicalBatchCode ??
    user?.student?.practical_batch_code ??
    user?.batch ??
    null;
  const studentBatchCode = studentBatchCodeRaw ? String(studentBatchCodeRaw).toUpperCase() : null;
  
  const { data: apiData, isLoading, error } = useStudentTimetable(studentId);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [viewMode, setViewMode] = useState("week");
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [todaySchedulesLoading, setTodaySchedulesLoading] = useState(false);
  const [todaySchedulesError, setTodaySchedulesError] = useState("");

  useEffect(() => {
    if (!studentClassId) return;
    const date = toDateString(new Date());
    (async () => {
      try {
        setTodaySchedulesLoading(true);
        setTodaySchedulesError("");
        const res = await scheduleService.getTimetable({ date, view: "daily" });
        const list = res?.data?.data || [];
        const filtered = Array.isArray(list)
          ? list.filter((s) => {
              if (String(s?.classId) !== String(studentClassId)) return false;
              const schedBatch = s?.practicalBatchCode ? String(s.practicalBatchCode).toUpperCase() : null;
              if (!schedBatch) return true; // lecture not split by batch
              if (!studentBatchCode) return false; // student has no batch but schedule is batch-specific
              return schedBatch === studentBatchCode;
            })
          : [];
        setTodaySchedules(filtered);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to load today's schedules";
        setTodaySchedulesError(msg);
        setTodaySchedules([]);
      } finally {
        setTodaySchedulesLoading(false);
      }
    })();
  }, [studentClassId, studentBatchCode]);

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Transform API data into a structured format
  const { timetableData, timeSlots } = useMemo(() => {
    // Check for nested data structure
    const timetableData = apiData?.data?.data || apiData?.data;
    
    if (!timetableData?.timeTableId || !timetableData.slots || timetableData.slots.length === 0) {
      return { timetableData: {}, timeSlots: [] };
    }

    const slots = timetableData.slots;
    const uniqueTimes = [...new Set(slots.map((slot) => 
      `${slot.startTime} - ${slot.endTime}`
    ))].sort();

    // Create date-based keys for the selected week
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 + (selectedWeek * 7)); // Monday of selected week
    
    const structuredData = {};
    days.forEach((day, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const displayKey = `${dateStr} (${day.slice(0, 3)})`;
      
      structuredData[displayKey] = slots
        .filter((slot) => slot.dayOfWeek.toLowerCase() === day.toLowerCase())
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
        .map((slot) => ({
          time: `${slot.startTime} - ${slot.endTime}`,
          subject: slot.subject?.name || "Free Period",
          teacher: slot.teacher?.name || "",
          room: slot.room || "",
          type: slot.type || "LECTURE",
          lectureType: slot.lectureType || "THEORY",
          meetingLink: slot.meetingLink || null,
          slotData: slot,
        }));
    });

    console.log("Structured timetable data:", structuredData);
    console.log("Unique time slots:", uniqueTimes);

    return { timetableData: structuredData, timeSlots: uniqueTimes };
  }, [apiData, selectedWeek]);

  const getSubjectColor = (subject) => {
    const colors = {
      Mathematics: "#1976d2",
      Physics: "#9c27b0",
      Chemistry: "#f44336",
      Biology: "#4caf50",
      English: "#ff9800",
      "Computer Science": "#00bcd4",
      History: "#795548",
      Geography: "#607d8b",
      Sports: "#e91e63",
      "Lunch Break": "#9e9e9e",
      Library: "#3f51b5",
      Music: "#673ab7",
      Art: "#ff5722",
      "Free Period": "#9e9e9e",
    };
    return colors[subject] || "#757575";
  };

  const getTodaySchedule = () => {
    // For the selected week, find today's equivalent day
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 + (selectedWeek * 7)); // Monday of selected week
    
    const targetDate = new Date(weekStart);
    targetDate.setDate(weekStart.getDate() + (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Convert to Monday-based index
    
    const dateStr = targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const dayName = days[dayOfWeek === 0 ? 6 : dayOfWeek - 1]; // Convert to our days array index
    const displayKey = `${dateStr} (${dayName.slice(0, 3)})`;
    
    return timetableData[displayKey] || [];
  };

  const getTimeSlotsToShow = () => {
    if (viewMode === "day") {
      // For day view, always show today's schedule
      const today = new Date();
      const dayName = today.toLocaleDateString("en-US", { weekday: "long" });
      
      // Filter slots directly for today's day
      const timetableData = apiData?.data?.data || apiData?.data;
      if (!timetableData?.slots) return [];
      
      const todaySlots = timetableData.slots
        .filter((slot) => slot.dayOfWeek.toLowerCase() === dayName.toLowerCase())
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
        
      return [...new Set(todaySlots.map((slot) => `${slot.startTime} - ${slot.endTime}`))].sort();
    }
    return timeSlots;
  };

  const getDaysToShow = () => {
    if (viewMode === "day") {
      // For day view, show today's date
      const today = new Date();
      return [today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' })];
    }
    
    // For week view, show actual dates for the selected week
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 + (selectedWeek * 7)); // Monday of selected week
    
    return days.map((day, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${dateStr} (${day.slice(0, 3)})`;
    });
  };

  const getWeekRange = (weekOffset = 0) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)); // Monday
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 4); // Friday
    
    const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
  };

  const handleDownload = () => {
    console.log("Downloading timetable...");
    // TODO: Implement download functionality
  };

  const handlePrint = () => {
    window.print();
  };

  if (!studentId) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Student ID not found. Please contact your administrator.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // Extract user-friendly message from API response
    const errorMessage = error.response?.data?.userMessage || 
                         error.response?.data?.message || 
                         error.message || 
                         "Unable to load timetable";
    
    const severity = error.response?.status === 404 ? "info" : "error";
    
    return (
      <Alert severity={severity} sx={{ mt: 2 }}>
        {errorMessage}
      </Alert>
    );
  }

  // Handle case where there's no timetable data but request was successful
  const apiTimetableData = apiData?.data?.data || apiData?.data;
  if (!apiTimetableData?.timeTableId) {
    const message = apiTimetableData?.userMessage || 
                    apiData?.message || 
                    "No timetable has been generated yet. Please contact your administrator.";
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Class Timetable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your weekly class schedule
            {apiTimetableData?.className && ` - ${apiTimetableData.className}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Today's Schedule */}
      <Card sx={{ mb: 3, bgcolor: "primary.main", color: "white" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Today />
            <Typography variant="h6">Today's Schedule</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1 }}>
            {todaySchedulesLoading ? (
              <Typography variant="body2">Loading schedules...</Typography>
            ) : todaySchedulesError ? (
              <Typography variant="body2">{todaySchedulesError}</Typography>
            ) : todaySchedules.length > 0 ? (
              todaySchedules.map((s, index) => (
                <Box
                  key={`${s.classId}-${s.subjectId}-${s.startTime}-${index}`}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    borderRadius: 2,
                    p: 2,
                    minWidth: 220,
                  }}
                >
                  <Typography variant="caption">
                    {String(s.startTime || "").slice(0, 5)} - {String(s.endTime || "").slice(0, 5)}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {s.subjectName || "—"}
                  </Typography>
                  {s.teacherName ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                      <Person sx={{ fontSize: 14 }} />
                      <Typography variant="caption">{s.teacherName}</Typography>
                    </Box>
                  ) : null}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    <Chip
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white" }}
                      label={String(s.lectureType || "LECTURE").toUpperCase()}
                    />
                    {s.practicalBatchCode ? (
                      <Chip
                        size="small"
                        sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white" }}
                        label={`Batch ${String(s.practicalBatchCode).toUpperCase()}`}
                      />
                    ) : null}
                    {s.isSwapped && (
                      <Chip
                        size="small"
                        label="Swapped"
                        color="info"
                      />
                    )}
                    {s.isEdited && (
                      <Chip
                        size="small"
                        label="Edited"
                        color="secondary"
                      />
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2">No classes today</Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Week Navigation */}
      {viewMode === "week" && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewMode}
                  label="View"
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <MenuItem value="week">Weekly View</MenuItem>
                  <MenuItem value="day">Daily View</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton onClick={() => setSelectedWeek(selectedWeek - 1)}>
                  <NavigateBefore />
                </IconButton>
                <Typography variant="subtitle1" fontWeight={500}>
                  {getWeekRange(selectedWeek)}
                </Typography>
                <IconButton onClick={() => setSelectedWeek(selectedWeek + 1)}>
                  <NavigateNext />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Daily View Header */}
      {viewMode === "day" && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewMode}
                  label="View"
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <MenuItem value="week">Weekly View</MenuItem>
                  <MenuItem value="day">Daily View</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="subtitle1" fontWeight={500}>
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric' 
                })} Schedule
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Timetable Grid */}
      <Card>
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: 600, bgcolor: "grey.100", minWidth: 120 }}
                >
                  Time
                </TableCell>
                {getDaysToShow().map((day) => (
                  <TableCell
                    key={day}
                    sx={{
                      fontWeight: 600,
                      bgcolor: "grey.100",
                      minWidth: 150,
                      textAlign: "center",
                    }}
                  >
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {getTimeSlotsToShow().map((time, timeIndex) => (
                <TableRow key={time}>
                  <TableCell sx={{ fontWeight: 500, bgcolor: "grey.50" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTime
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      {time}
                    </Box>
                  </TableCell>
                  {getDaysToShow().map((day) => {
                    let slot;
                    if (viewMode === "day") {
                      // For daily view, get today's schedule directly from API data
                      const timetableData = apiData?.data?.data || apiData?.data;
                      const today = new Date();
                      const dayName = today.toLocaleDateString("en-US", { weekday: "long" });
                      
                      const todaySlots = timetableData?.slots
                        ?.filter((s) => s.dayOfWeek.toLowerCase() === dayName.toLowerCase())
                        ?.sort((a, b) => a.startTime.localeCompare(b.startTime)) || [];
                      
                      slot = todaySlots.find((s) => `${s.startTime} - ${s.endTime}` === time);
                      if (slot) {
                        slot = {
                          time: `${slot.startTime} - ${slot.endTime}`,
                          subject: slot.subject?.name || "Free Period",
                          teacher: slot.teacher?.name || "",
                          room: slot.room || "",
                          type: slot.type || "LECTURE",
                          lectureType: slot.lectureType || "THEORY",
                          meetingLink: slot.meetingLink || null,
                          slotData: slot,
                        };
                      }
                    } else {
                      // For week view, use the processed timetableData
                      slot = timetableData[day]?.find((s) => s.time === time);
                    }
                    
                    if (!slot || !slot.subject || slot.subject === "Free Period") {
                      return (
                        <TableCell key={day} sx={{ bgcolor: "grey.50" }}>
                          <Typography variant="caption" color="text.secondary">
                            Free
                          </Typography>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={day}>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2">
                                {slot.subject}
                              </Typography>
                              {slot.teacher && (
                                <Typography variant="caption">
                                  {slot.teacher}
                                </Typography>
                              )}
                              <br />
                              <Typography variant="caption">
                                {slot.room}
                              </Typography>
                              <br />
                              <Typography variant="caption">
                                Type: {slot.type}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              bgcolor:
                                slot.subject === "Lunch Break"
                                  ? "grey.200"
                                  : `${getSubjectColor(slot.subject)}15`,
                              borderLeft:
                                slot.subject !== "Lunch Break"
                                  ? `3px solid ${getSubjectColor(slot.subject)}`
                                  : "none",
                              borderRadius: 1,
                              p: 1,
                              cursor: "pointer",
                              "&:hover": { opacity: 0.8 },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ color: getSubjectColor(slot.subject) }}
                            >
                              {slot.subject}
                            </Typography>
                            {slot.teacher && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {slot.teacher}
                              </Typography>
                            )}
                            {slot.room && (
                              <Chip
                                icon={<Room sx={{ fontSize: 12 }} />}
                                label={slot.room}
                                size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                              />
                            )}
                            {slot.slotData?.isSwapped && (
                              <Chip
                                label="Swapped"
                                color="info"
                                size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                              />
                            )}
                            {slot.slotData?.isEdited && (
                              <Chip
                                label="Edited"
                                color="secondary"
                                size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                              />
                            )}
                            {slot.lectureType === "ONLINE" && slot.meetingLink && (
                              <Chip
                                label="Join Online"
                                size="small"
                                component="a"
                                href={slot.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                color="info"
                                sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Legend */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Subject Colors
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.entries({
              Mathematics: "#1976d2",
              Physics: "#9c27b0",
              Chemistry: "#f44336",
              Biology: "#4caf50",
              English: "#ff9800",
              "Computer Science": "#00bcd4",
            }).map(([subject, color]) => (
              <Chip
                key={subject}
                label={subject}
                size="small"
                sx={{
                  bgcolor: `${color}15`,
                  color: color,
                  borderLeft: `3px solid ${color}`,
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Timetable;
